import NextLink from "next/link";

import { Link } from "@releaseit/ui";

import { NavigationLink, NavigationQuery } from "@/generated/graphql";

import { NavCategory } from "../../types";

type DesktopCategoriesNavProps = {
  categories: NavigationQuery["Navigation"]["categories"];
};

export function DesktopCategoriesNav({ categories }: DesktopCategoriesNavProps) {
  return (
    <>
      <style jsx>{`
        .desktop-categories > :global(:first-child) {
          margin-top: 0;
        }
      `}</style>

      <div className="desktop-categories container my-16 hidden gap-8 space-y-8 md:block md:columns-2 lg:columns-3">
        {categories.map((category) => (
          <NavItem key={category.title} itemType="category" item={category} />
        ))}
      </div>
    </>
  );
}

/** Internal components */

type NavItemProps =
  // Can either be a category (node or leaf)
  | {
      itemType: "category";
      item: NavCategory;
    }
  // Or a brand (always a leaf)
  | {
      itemType: "brand";
      item: NavigationLink;
    };

function NavItem({ itemType, item }: NavItemProps) {
  const hrefBase = itemType === "category" ? "/hire" : "/brand";

  // If leaf node (or brand since brand is always a leaf node), just render link
  if (itemType === "brand" || !item.children || item.children.length === 0) {
    return (
      <NextLink href={`${hrefBase}/${item.href}`} prefetch={false} passHref>
        <Link className="text-gray-800 no-underline">{item.title} &rarr;</Link>
      </NextLink>
    );
  }

  // Otherwise, recursively render another category
  return (
    <div className="mt-4 break-inside-avoid rounded-lg border p-4">
      <NextLink href={`${hrefBase}/${item.href}`} prefetch={false} passHref>
        <Link className="font-medium text-black no-underline">{item.title}</Link>
      </NextLink>

      <ul className="mt-2">
        {item.children
          .sort((child) => (child.children && child.children.length > 0 ? 1 : -1))
          .map((child) => (
            <li key={child.title} className="truncate">
              <NavItem itemType="category" item={child} />
            </li>
          ))}
      </ul>

      {item.__typename === "NavigationTopCategory" && (
        <div>
          <p className="mt-4 font-medium text-black">Popular brands</p>
          <ul className="mt-2">
            {item.popularBrands.map((child) => (
              <li key={child.title} className="truncate">
                <NavItem itemType="brand" item={child} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
